import * as offersActions from '@/_store/offers/offers.actions';
import {
	searchFilterOptions,
	selectAvailableBoardsDialog,
	selectFilterOptions,
	selectIsOptionsAreEmpty,
	selectNextOffers,
	selectNextPageIsLoading,
	selectNightCounters,
	selectNightsRows,
	selectOfferList,
	selectOfferListCount,
	selectOfferListHeader,
	selectOfferListHeaderByKey,
	selectOffersDateRange,
	selectOffersOffset,
	selectOptions,
	selectParticipantCounters,
	selectParticipantsRows,
	selectPriceRange,
	selectSelectedDateRange,
	selectSelectedOptionsParams,
	selectSelectedValues,
	selectSorting,
	selectTransportKind,
} from '@/_store/offers/offers.selectors';
import {
	BaseFilter,
	DEFAULT_PAGINATION_LIMIT,
	DEFAULT_PAGINATION_OFFSET,
	NumericDictionary,
	OfferDateRange,
	OfferList,
	Selected,
	SortingOptions,
} from '@/_store/offers/offers.types';
import { PriceRange } from '@/new-top-search/top-search-dropdown-item/price-filter/price-filter.component';
import {
	RowValue,
	SearchResult,
	TopSearchDropdownItemMode,
} from '@/new-top-search/top-search-dropdown-item/top-search-dropdown-item.types';
import { TransportKind } from '@/offer/offer.model';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, take } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class OffersFacade {
	offersDateRange$ = this.store.select(selectOffersDateRange);
	selectedDateRange$ = this.store.select(selectSelectedDateRange);
	participantsRows$ = this.store.select(selectParticipantsRows);
	nightsRows$ = this.store.select(selectNightsRows);
	participantCounters$ = this.store.select(selectParticipantCounters);
	nightCounters$ = this.store.select(selectNightCounters);
	offersList$ = this.store.select(selectOfferList);
	availableBoards$ = this.store.select(selectAvailableBoardsDialog);
	urlParams$ = this.store.select(selectSelectedOptionsParams);
	selected$ = this.store.select(selectSelectedValues);
	headerValues$ = this.store.select(selectOfferListHeader);
	offerListCount$ = this.store.select(selectOfferListCount);
	transportKind$ = this.store.select(selectTransportKind);
	sorting$ = this.store.select(selectSorting);
	offset$ = this.store.select(selectOffersOffset);
	nextOffers$ = this.store.select(selectNextOffers);
	nextPageIsLoading$ = this.store.select(selectNextPageIsLoading);
	filterOptions$ = this.store.select(selectFilterOptions);
	offerFiltersAreEmpty$ = this.store.select(selectIsOptionsAreEmpty);
	priceRange$ = this.store.select(selectPriceRange);

	constructor(private readonly store: Store) {}

	fetchOfferFilter(): void {
		this.store.dispatch(offersActions.fetchOfferFilter());
	}

	fetchOfferList(params: string, paramsForNextPage: string): void {
		this.store.dispatch(offersActions.fetchOffers({ params, paramsForNextPage }));
	}

	fetchNextOfferList(params: string): void {
		this.store.dispatch(offersActions.fetchNextOffers({ params }));
	}

	fetchBoards(id: number): void {
		this.store.dispatch(offersActions.fetchBoards({ id }));
	}

	getOptions(mode: TopSearchDropdownItemMode): Observable<BaseFilter[]> {
		return this.store.select(selectOptions(mode));
	}

	getHeaderValue(key: TopSearchDropdownItemMode): Observable<string | number> {
		return this.store.select(selectOfferListHeaderByKey(key));
	}

	addLocationsFilterOption(id: number): void {
		this.store.dispatch(
			offersActions.addLocationFilterOption({
				id,
			}),
		);
	}

	removeLocationsFilterOption(id: number): void {
		this.store.dispatch(offersActions.removeLocationFilterOption({ id }));
	}

	addKindsFilterOption(id: number): void {
		this.store.dispatch(
			offersActions.addKindsFilterOption({
				id,
			}),
		);
	}

	removeKindsFilterOption(id: number): void {
		this.store.dispatch(offersActions.removeKindsFilterOption({ id }));
	}

	addBoardsFilterOption(id: number): void {
		this.store.dispatch(
			offersActions.addBoardsFilterOption({
				id,
			}),
		);
	}

	removeBoardsFilterOption(id: number): void {
		this.store.dispatch(offersActions.removeBoardsFilterOption({ id }));
	}

	addTagsFilterOption(id: number): void {
		this.store.dispatch(
			offersActions.addTagsFilterOption({
				id,
			}),
		);
	}

	removeTagsFilterOption(id: number): void {
		this.store.dispatch(offersActions.removeTagsFilterOption({ id }));
	}

	addConveniencesFilterOption(id: number): void {
		this.store.dispatch(offersActions.addConveniencesFilterOption({ id }));
	}

	removeConveniencesFilterOption(id: number): void {
		this.store.dispatch(offersActions.removeConveniencesFilterOption({ id }));
	}

	addStopsFilterOption(id: number): void {
		this.store.dispatch(offersActions.addStopsFilterOption({ id }));
	}

	removeStopsFilterOption(id: number): void {
		this.store.dispatch(offersActions.removeStopsFilterOption({ id }));
	}

	updateOffersDateRange(dateRange: OfferDateRange): void {
		this.store.dispatch(offersActions.updateOffersDateRange({ dateRange }));
	}

	updateAdultsCount(adults: number): void {
		this.store.dispatch(offersActions.updateAdultsCount({ adults }));
	}

	updateChildrenCount(children: number): void {
		this.store.dispatch(offersActions.updateChildrenCount({ children }));
	}

	updateNights(row: RowValue): void {
		this.store.dispatch(offersActions.updateNights({ row }));
	}

	updateNightsOptions(option: RowValue): void {
		this.store.dispatch(offersActions.updateNightsOptions({ option }));
	}

	updateQueryParams(params: string): void {
		this.store.dispatch(offersActions.updateQueryParams({ params }));
	}

	updateOwnTransport(ownTransport: boolean): void {
		this.store.dispatch(offersActions.updateOwnTransport({ ownTransport }));
	}

	updateSearchFilter(search: string): void {
		this.store.dispatch(offersActions.updateSearchFilter({ search }));
	}

	updateTransportKind(transportKind: TransportKind, value: boolean): void {
		this.store.dispatch(offersActions.updateTransportKind({ transportKind, value }));
	}

	updatePriceRange(priceRange: PriceRange): void {
		this.store.dispatch(offersActions.updatePriceRange({ priceRange }));
	}

	clearStopsByTransportKind(transportKind: TransportKind): void {
		this.store.dispatch(offersActions.clearStopsByTransportKind({ transportKind }));
	}

	updateSelectedProducts(products: number[]): void {
		this.store.dispatch(offersActions.updateSelectedProducts({ products }));
	}

	clearAvailableBoards(): void {
		this.store.dispatch(offersActions.clearAvailableBoards());
	}

	clearOfferList(): void {
		this.store.dispatch(offersActions.clearOfferList());
	}

	clearBoardsFilter(boardId?: number): void {
		this.store.dispatch(offersActions.clearBoardsFilter({ boardId }));
	}

	clearConveniencesFilter(convenienceId?: number): void {
		this.store.dispatch(offersActions.clearConveniencesFilter({ convenienceId }));
	}

	clearStopsFilter(stopId?: number): void {
		this.store.dispatch(offersActions.clearStopsFilter({ stopId }));
	}

	clearLocationsFilter(locationId?: number): void {
		this.store.dispatch(offersActions.clearLocationsFilter({ locationId }));
	}

	clearKindsFilter(kindId?: number): void {
		this.store.dispatch(offersActions.clearKindsFilter({ kindId }));
	}

	clearTagsFilter(tagId: number): void {
		this.store.dispatch(offersActions.clearTagsFilter({ tagId }));
	}

	clearSearchFilter(): void {
		this.store.dispatch(offersActions.clearSearchFilter());
	}

	clearPriceRange(): void {
		this.store.dispatch(offersActions.clearPriceRange());
	}

	clearTransportKind(transportKind: TransportKind): void {
		if (!transportKind) {
			return;
		}
		this.store.dispatch(offersActions.clearTransportKind({ transportKind }));
	}

	setDefaultNights(): void {
		this.store.dispatch(offersActions.setDefaultNights());
	}

	setDefaultAdultValue(): void {
		this.store.dispatch(offersActions.setDefaultAdultValue());
	}

	setDefaultChildrenValue(): void {
		this.store.dispatch(offersActions.setDefaultChildrenValue());
	}

	clearSelectedOptions(): void {
		this.store.dispatch(offersActions.clearSelectedOptions());
	}

	clearSelectedProducts(): void {
		this.store.dispatch(offersActions.clearSelectedProducts());
	}

	setInitialState(): void {
		this.store.dispatch(offersActions.setInitialState());
	}

	setSorting(sorting: SortingOptions): void {
		this.store.dispatch(offersActions.setSorting({ sorting }));
	}

	updateOffset(offset: number): void {
		this.store.dispatch(offersActions.updateOffset({ offset }));
	}

	updateOfferList(offersList: NumericDictionary<OfferList>): void {
		this.store.dispatch(offersActions.updateOffers({ offersList }));
	}

	clearOffersNextPage(): void {
		this.store.dispatch(offersActions.clearOffersNextPage());
	}

	updateNextPageIsLoading(nextPageIsLoading: boolean): void {
		this.store.dispatch(offersActions.updateNextPageIsLoading({ nextPageIsLoading }));
	}

	reloadOfferList(): void {
		this.updateOffset(DEFAULT_PAGINATION_OFFSET);
		combineLatest([this.urlParams$, this.offset$])
			.pipe(take(1))
			.subscribe(([params, offset]) => {
				this.clearOfferList();
				this.fetchOfferList(
					params + `&limit=${DEFAULT_PAGINATION_LIMIT}&offset=${offset}`,
					params + `&limit=${DEFAULT_PAGINATION_LIMIT}&offset=${offset + DEFAULT_PAGINATION_LIMIT}`,
				);
				this.updateQueryParams(params);
			});
	}

	updateSelectedOptions(selected: Selected): void {
		this.store.dispatch(offersActions.updateSelectedValues({ selected }));
	}

	searchOffersBySearchTerm(searchTerm: string): Observable<SearchResult> {
		return this.store.select(searchFilterOptions(searchTerm));
	}
}
